import { makeObservable, observable, action, flow } from "mobx";
import { getUserListFilters, getUserList, getUserByUuid } from "../../../services/users";

class UsersStore {
  users = [];
  currentUser = null;
  isLoading = false;
  isLoadingUser = false;
  error = null;
  meta = {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 20,
    hasNext: false,
    hasPrevious: false,
    nextCursor: null,
  };

  constructor() {
    makeObservable(this, {
      // Observables
      users: observable,
      currentUser: observable,
      isLoading: observable,
      isLoadingUser: observable,
      error: observable,
      meta: observable,

      // Actions
      setUsers: action,
      setCurrentUser: action,
      setLoading: action,
      setError: action,
      setMeta: action,
      handleApiResponse: action,
      handleApiError: action,

      // Flows (async actions)
      fetchUsersFilters: flow,
      getUserList: flow,
      getUserByUuid: flow,
    });
  }

  // Actions
  setUsers = (users) => {
    this.users = users;
  };

  setCurrentUser = (user) => {
    this.currentUser = user;
  };

  setLoading = (status) => {
    this.isLoading = status;
  };

  setError = (error) => {
    this.error = error;
  };

  setMeta = (meta) => {
    this.meta = meta;
  };

  /**
   * Handles API response and extracts error messages
   * @param {Object} response - API response
   * @param {string} defaultErrorMessage - Default error message if extraction fails
   * @returns {Object} Parsed response with statusCode, msgCode, body, meta
   * @throws {Error} If response indicates an error
   */
  handleApiResponse(response, defaultErrorMessage) {
    if (!response) {
      throw new Error('Invalid response from server');
    }

    // Handle both snake_case and camelCase formats
    const statusCode = response.status_code || response.statusCode || 200;
    const msgCode = response.msg_code || response.msgCode || [];
    const responseBody = response.body;
    const meta = response.meta || null;

    // Success (2xx)
    if (statusCode >= 200 && statusCode < 300) {
      return {
        statusCode,
        msgCode,
        body: responseBody,
        meta,
      };
    }

    // Error - extract message from msgCode (combine all messages)
    let errorMessage = defaultErrorMessage;
    if (msgCode.length > 0) {
      // Combine all error messages from msgCode array
      errorMessage = msgCode
        .map(msg => msg.description || msg.code)
        .filter(Boolean)
        .join(', ') || defaultErrorMessage;
    }

    this.setError(errorMessage);
    
    const error = new Error(errorMessage);
    error.response = {
      data: response,
      status: statusCode,
    };
    
    throw error;
  }

  /**
   * Handles caught errors from API calls
   * @param {Error} error - Caught error
   * @param {string} defaultErrorMessage - Default error message
   * @throws {Error} Processed error
   */
  handleApiError(error, defaultErrorMessage) {
    // If error already has a message from handleApiResponse, use it
    if (error.message && error.message !== defaultErrorMessage) {
      // Error message was already set by handleApiResponse
      // Just ensure error state is set (it should already be)
      if (!this.error) {
        this.setError(error.message);
      }
      throw error;
    }

    // Handle network errors or API errors
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      
      // Try to extract message from msgCode array (API format)
      let errorMessage = defaultErrorMessage;
      const msgCode = responseData.msg_code || responseData.msgCode || [];
      if (msgCode.length > 0) {
        // Combine all error messages from msgCode
        errorMessage = msgCode
          .map(msg => msg.description || msg.code)
          .filter(Boolean)
          .join(', ') || defaultErrorMessage;
      } else {
        // Fallback to other message fields
        errorMessage = responseData.message || error.message || defaultErrorMessage;
      }

      this.setError(errorMessage);
      
      const apiError = new Error(errorMessage);
      apiError.response = {
        data: responseData,
        status: error.response.status,
      };
      
      throw apiError;
    }

    // Handle other errors (network, etc.)
    const errorMessage = error.message || defaultErrorMessage;
    this.setError(errorMessage);
    throw error;
  }

  // API Actions
  *fetchUsersFilters(params = {}) {
    // ✅ MEMOIZATION: Skip if users are already loaded (unless force reload with params)
    if (this.users.length > 0 && Object.keys(params).length === 0) {
      return;
    }

    try {
      this.isLoading = true;
      this.error = null;

      const response = yield getUserListFilters(params);
      const parsed = this.handleApiResponse(response, 'An error occurred while fetching users list.');

      // Set users from response body
      this.setUsers(Array.isArray(parsed.body) ? parsed.body : []);

      // Set metadata
      if (parsed.meta) {
        this.setMeta(parsed.meta);
      }
    } catch (error) {
      this.handleApiError(error, 'An error occurred while fetching users list.');
      this.setUsers([]); // Clear list on error
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get user list (uses /v1/tenant/user/list endpoint)
   * Calls the getUserList service and handles response with handlers
   * 
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @param {string} params.order_by - Field to order by
   * @param {string} params.order_direction - Order direction (ASC/DESC)
   * @param {string|Object} params.search - Search keyword or search object
   * @returns {Promise<void>}
   */
  *getUserList(params = {}) {
    try {
      this.isLoading = true;
      this.error = null;

      // Call the service
      const response = yield getUserList(params);

      // Use handleApiResponse to normalize and handle errors
      const parsed = this.handleApiResponse(response, 'An error occurred while fetching users list.');

      // Set users from response body
      this.setUsers(Array.isArray(parsed.body) ? parsed.body : []);

      // Set metadata
      if (parsed.meta) {
        this.setMeta(parsed.meta);
      }
    } catch (error) {
      this.handleApiError(error, 'An error occurred while fetching users list.');
      this.setUsers([]); // Clear list on error
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get user by UUID (uses /v1/tenant/user/{userUuid} endpoint)
   * Calls the getUserByUuid service and handles response with handlers
   * 
   * @param {string} userUuid - User UUID
   * @returns {Promise<Object>} User data
   */
  *getUserByUuid(userUuid) {
    if (!userUuid) {
      throw new Error('userUuid is required');
    }

    try {
      this.isLoadingUser = true;
      this.error = null;

      // Call the service
      const response = yield getUserByUuid(userUuid);

      // Use handleApiResponse to normalize and handle errors
      const parsed = this.handleApiResponse(response, 'An error occurred while fetching user.');

      // Set current user from response body
      this.setCurrentUser(parsed.body || null);

      return parsed.body;
    } catch (error) {
      this.handleApiError(error, 'An error occurred while fetching user.');
      this.setCurrentUser(null);
      throw error;
    } finally {
      this.isLoadingUser = false;
    }
  }
}

export default UsersStore;

